import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { formatClientTicketCreateDenialMessage } from "@/lib/auth/client-ticket-create-eval";
import { resolveClientTicketCreateDenialReason } from "@/lib/auth/resolve-client-ticket-create-access";
import { HUB_ROLE } from "@/lib/auth/roles";
import { getSupabase, withTimeout } from "@/lib/hooks/api/utils";
import { getSafeErrorMessage } from "@/lib/utils/safe-error";
import { useBustDashboardCache } from "@/lib/hooks/api/dashboard/use-dashboard-summary";
import { TICKET_PRIORITIES } from "@/lib/constants/ticket-priorities";
import { TICKET_CATEGORY_VALUES } from "@/lib/constants/ticket-categories";

export function useCreateTicket() {
  const queryClient = useQueryClient();
  const bustDashboard = useBustDashboardCache();
  const ticketCreateSchema = z.object({
    title: z.string().min(3),
    description: z.string().min(1),
    contact_id: z.string().uuid(),
    priority: z.enum(TICKET_PRIORITIES),
    category: z.enum(TICKET_CATEGORY_VALUES),
    estimated_hours: z.preprocess(
      (val) => (val === "" || val === undefined ? undefined : Number(val)),
      z.number().positive().optional()
    ),
    due_date: z.string().optional(),
    tags: z.array(z.string()).optional(),
    internal_notes: z.string().optional(),
    customer_visible_notes: z.string().optional(),
  });

  return useMutation({
    mutationFn: async (ticketData: {
      title: string;
      description: string;
      contact_id: string;
      priority: string;
      category: string;
      estimated_hours?: number;
      due_date?: string;
      tags?: string[];
      internal_notes?: string;
      customer_visible_notes?: string;
    }) => {
      const titleTrim = typeof ticketData.title === "string" ? ticketData.title.trim() : "";
      const descTrim =
        typeof ticketData.description === "string" ? ticketData.description.trim() : "";
      if (!titleTrim || !descTrim) {
        throw new Error("Title and description are required.");
      }

      const supabase = getSupabase();
      let validated: z.infer<typeof ticketCreateSchema>;
      try {
        validated = ticketCreateSchema.parse(ticketData);
      } catch (err) {
        throw new Error(getSafeErrorMessage(err));
      }

      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || !authData?.user) {
        throw new Error("User not authenticated");
      }

      const { data: appUser } = await supabase
        .from("app_users")
        .select("role")
        .eq("id", authData.user.id)
        .maybeSingle();

      const isClientUser = appUser?.role === HUB_ROLE.CLIENT;
      let clientDenialReason:
        | Awaited<ReturnType<typeof resolveClientTicketCreateDenialReason>>
        | null = null;

      if (isClientUser) {
        clientDenialReason = await resolveClientTicketCreateDenialReason(
          supabase,
          authData.user.id,
          validated.contact_id,
        );
        if (clientDenialReason !== "allowed") {
          throw new Error(formatClientTicketCreateDenialMessage(clientDenialReason));
        }
      }

      const payload = { ...validated, created_by: authData.user.id };
      const { data, error } = await withTimeout(
        supabase.from("tickets").insert([payload]).select().single(),
        15000,
        "create ticket"
      );

      if (error) {
        console.error("Error creating ticket:", error);
        const isRlsDenial =
          error.code === "42501" ||
          /row-level security/i.test(error.message ?? "");

        if (isRlsDenial && isClientUser) {
          const reason =
            clientDenialReason === "allowed"
              ? "allowed_but_blocked"
              : await resolveClientTicketCreateDenialReason(
                  supabase,
                  authData.user.id,
                  validated.contact_id,
                );
          throw new Error(
            formatClientTicketCreateDenialMessage(
              reason === "allowed" ? "allowed_but_blocked" : reason,
            ),
          );
        }

        throw new Error(isRlsDenial ? "You don't have permission to create this ticket." : error.message);
      }

      // Notify the client Slack channel via the canonical server path. Mirrors
      // the GraphQL create hook; best-effort, never blocks ticket creation.
      if (data?.contact_id) {
        try {
          await fetch("/api/tickets/webhook", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
          });
        } catch (webhookError) {
          console.error("Failed to send ticket Slack notification:", webhookError);
        }
      }

      return data;
    },
    retry: 0,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
      void bustDashboard(); // new ticket changes dashboard critical tickets + recent activity

      if (data?.tags) {
        void queryClient.invalidateQueries({ queryKey: ["tags", "tickets"] });
        fetch("/api/tags/invalidate/tickets", { method: "POST" }).catch((error) => {
          console.error("Failed to invalidate server-side ticket tags cache:", error);
        });
      }

      toast.success("Ticket created successfully");
    },
    onError: (error) => {
      toast.error(getSafeErrorMessage(error));
      console.error("Error creating ticket:", error);
    },
  });
}
